import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { BookOpen, Menu, Sun, Moon } from "lucide-react";
import { catalog } from "./content/catalog";
import { isComplete, useProgress } from "./store/progress";
import { useUI } from "./store/ui";
import Splitter from "./components/layout/Splitter";
import CourseSidebar from "./components/layout/CourseSidebar";
import PageErrorBoundary from "./components/layout/PageErrorBoundary";
import { useActivity } from "./store/useActivity";
const LearnPage = lazy(() => import("./pages/LearnPage"));
const QuizPage = lazy(() => import("./pages/QuizPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const MistakesPage = lazy(() => import("./pages/MistakesPage"));

export default function App() {
  const modules = catalog.modules;
  const [drawer, setDrawer] = useState(false);
  const drawerOpener = useRef<HTMLButtonElement>(null);
  const {
    theme,
    setTheme,
    sidebarWidth,
    editorPercent,
    setLayout,
    preferenceError,
  } = useUI();
  const { data, hydrate, hydrated, saveState, flush, storageMessage } =
    useProgress();
  const [systemDark, setSystemDark] = useState(
    matchMedia("(prefers-color-scheme: dark)").matches,
  );
  const dark = theme === "dark" || (theme === "system" && systemDark);
  const location = useLocation();
  useActivity(
    hydrated &&
      (location.pathname.startsWith("/learn/") ||
        location.pathname.startsWith("/quiz/")),
  );
  useEffect(() => {
    void hydrate();
  }, [hydrate]);
  useEffect(() => {
    const query = matchMedia("(prefers-color-scheme: dark)");
    const handle = () => setSystemDark(query.matches);
    query.addEventListener("change", handle);
    return () => query.removeEventListener("change", handle);
  }, []);
  useEffect(() => {
    document.documentElement.dataset.theme = dark ? "dark" : "light";
  }, [dark]);
  useEffect(() => {
    const save = () => {
      if (document.hidden) void flush();
    };
    document.addEventListener("visibilitychange", save);
    return () => document.removeEventListener("visibilitychange", save);
  }, [flush]);
  const lessons = modules.flatMap((module) => module.lessons);
  const completed = modules.reduce(
    (sum, module) =>
      sum +
      module.lessons.filter((lesson) =>
        isComplete(
          lesson.id,
          module.quiz.id,
          data,
          module.quiz.passScorePercent,
        ),
      ).length,
    0,
  );
  if (!hydrated)
    return (
      <main className="loading-shell">
        <div className="brand-mark">zh</div>
        <h1>前端学习工作台</h1>
        <p>正在准备课程与本地学习记录…</p>
      </main>
    );
  return (
    <div
      className="studio-shell"
      style={{ "--sidebar-width": `${sidebarWidth}px` } as React.CSSProperties}
    >
      <a
        className="skip-link"
        href="#learning-main"
        onClick={(event) => {
          event.preventDefault();
          document.getElementById("learning-main")?.focus();
        }}
      >
        跳到学习内容
      </a>
      <CourseSidebar
        modules={modules}
        progress={data}
        open={drawer}
        setOpen={setDrawer}
        opener={drawerOpener}
      />
      <div
        className="sidebar-resize"
        role="region"
        aria-label="课程目录宽度控制"
      >
        <Splitter
          label="调整课程目录宽度"
          value={sidebarWidth}
          min={200}
          max={320}
          onChange={(width) => setLayout(width, editorPercent)}
        />
      </div>
      <div className="studio-main">
        <header className="studio-header">
          <button
            ref={drawerOpener}
            className="menu-button"
            aria-label="打开课程目录"
            aria-expanded={drawer}
            aria-controls="course-navigation"
            aria-haspopup="dialog"
            onClick={() => setDrawer(!drawer)}
          >
            <Menu size={20} />
          </button>
          <div className="breadcrumb">
            <span>我的空间</span>
            <span>/</span>
            <strong>前端学习工作台</strong>
          </div>
          <div className="header-tools">
            <div className="progress-chip">
              <span>
                {completed} / {lessons.length} 节
              </span>
              <progress
                value={completed}
                max={lessons.length}
                aria-label="课程进度"
              />
            </div>
            <span className={`save-indicator ${saveState}`} role="status">
              {saveState === "saved"
                ? "已保存"
                : saveState === "saving"
                  ? "保存中…"
                  : "保存失败，请备份"}
            </span>
            <button
              aria-label={dark ? "切换浅色主题" : "切换深色主题"}
              onClick={() => setTheme(dark ? "light" : "dark")}
            >
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
        </header>
        {(storageMessage || preferenceError) && (
          <div className="storage-notice" role="alert">
            {storageMessage ||
              "浏览器禁止保存界面偏好，主题和布局仅在本次打开期间生效。"}
          </div>
        )}
        <main
          id="learning-main"
          className="workspace-content"
          tabIndex={-1}
          aria-label="学习内容"
        >
          <PageErrorBoundary
            routeKey={`${location.pathname}${location.search}`}
          >
            <Suspense
              fallback={<div className="loading">正在打开学习面板…</div>}
            >
              <Routes>
                <Route
                  path="/"
                  element={<Navigate replace to="/learn/react-state" />}
                />
                <Route
                  path="/learn/:lessonId"
                  element={<LearnPage dark={dark} />}
                />
                <Route
                  path="/quiz/:moduleId"
                  element={<QuizPage key={location.pathname} dark={dark} />}
                />
                <Route
                  path="/profile"
                  element={<ProfilePage modules={modules} />}
                />
                <Route path="/mistakes" element={<MistakesPage />} />
                <Route
                  path="*"
                  element={
                    <div className="empty-state">
                      <BookOpen />
                      <h1>这一页暂时找不到</h1>
                      <Link to="/">返回课程</Link>
                    </div>
                  }
                />
              </Routes>
            </Suspense>
          </PageErrorBoundary>
        </main>
      </div>
    </div>
  );
}
